import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    CASE_TYPE_OPTIONS,
    POLE_OPTIONS,
    STATUS_OPTIONS,
    RISK_CLASSIFICATION_OPTIONS,
    CREDITOR_ENTITY_OPTIONS,
    LegalCaseType,
    Pole,
    LegalCaseStatus,
    type Beneficiary,
    type RiskClassificationOption,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Só escritórios entram no select de "escritório responsável" (o campo é pra saber quem cuida
// do caso). O backend guarda um beneficiário genérico; o filtro é de UX.
const onlyLawFirms = (list: Beneficiary[]): Beneficiary[] =>
    list.filter((b) => b.beneficiaryType === 'LAW_FIRM');

// Percentual sugerido para uma classificação CPC 25, a partir das opções carregadas. Fora do
// componente (sem função dentro de função). None → 0.
const suggestedPercentFor = (
    classification: string,
    options: RiskClassificationOption[],
): number => options.find((o) => o.value === classification)?.suggestedLossPercent ?? 0;

interface UseLegalCasePageFormArgs {
    onClose: () => void;
    onCreated: () => void;
}

export default function useLegalCasePageForm({ onClose, onCreated }: UseLegalCasePageFormArgs) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    const [caseType, setCaseType] = useState<string>(LegalCaseType.JUDICIAL_EXECUTION);
    const [pole, setPole] = useState<string>(Pole.ACTIVE);
    const [status, setStatus] = useState<string>(LegalCaseStatus.OPEN);
    const [creditorEntity, setCreditorEntity] = useState('');
    const [openedAt, setOpenedAt] = useState('');
    const [externalCaseNumber, setExternalCaseNumber] = useState('');
    const [court, setCourt] = useState('');
    const [district, setDistrict] = useState('');
    const [courtDivision, setCourtDivision] = useState('');
    const [state, setState] = useState('');
    const [courtLink, setCourtLink] = useState('');
    const [consultationPassword, setConsultationPassword] = useState('');
    const [claimValue, setClaimValue] = useState(0);
    const [responsibleFirmId, setResponsibleFirmId] = useState('');
    const [cpc25Classification, setCpc25ClassificationRaw] = useState('');
    const [estimatedLossPercent, setEstimatedLossPercent] = useState(0);
    const [provisionOverrideAmount, setProvisionOverrideAmount] = useState(0);
    const [provisionOverrideReason, setProvisionOverrideReason] = useState('');
    const [description, setDescription] = useState('');

    // Opções dos selects vindas do backend, com fallback local.
    const [caseTypeOptions, setCaseTypeOptions] = useState<SelectOption[]>(CASE_TYPE_OPTIONS);
    const [poleOptions, setPoleOptions] = useState<SelectOption[]>(POLE_OPTIONS);
    const [statusOptions, setStatusOptions] = useState<SelectOption[]>(STATUS_OPTIONS);
    const [riskClassificationOptions, setRiskClassificationOptions] =
        useState<RiskClassificationOption[]>(RISK_CLASSIFICATION_OPTIONS);
    const [creditorEntityOptions, setCreditorEntityOptions] =
        useState<SelectOption[]>(CREDITOR_ENTITY_OPTIONS);
    // Escritórios cadastrados (registro global) para o select de escritório responsável.
    const [lawFirms, setLawFirms] = useState<Beneficiary[]>([]);

    // Ao escolher a classificação, pré-preenche o percentual com a sugestão (editável — ADR 003:
    // "não deixar números percentuais soltos"). Limpar a classificação zera o percentual.
    const setCpc25Classification = (value: string) => {
        setCpc25ClassificationRaw(value);
        setEstimatedLossPercent(value ? suggestedPercentFor(value, riskClassificationOptions) : 0);
    };

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.caseTypes?.length) setCaseTypeOptions(opts.caseTypes);
                if (opts.poles?.length) setPoleOptions(opts.poles);
                if (opts.statuses?.length) setStatusOptions(opts.statuses);
                if (opts.riskClassifications?.length) setRiskClassificationOptions(opts.riskClassifications);
                if (opts.creditorEntities?.length) setCreditorEntityOptions(opts.creditorEntities);
            } catch {
                // silencioso — fallback local já preenche os selects
            }
        };
        loadOptions();
    }, []);

    useEffect(() => {
        recoveryApi.getBeneficiaries({ type: 'LAW_FIRM' })
            .then((result) => setLawFirms(onlyLawFirms(result.items || [])))
            .catch(() => { /* silencioso — o select fica vazio se falhar */ });
    }, []);

    const handleCreate = async () => {
        setIsCreating(true);
        setErrorFields([]);
        try {
            await recoveryApi.createLegalCase({
                caseType,
                pole,
                status,
                creditorEntity,
                openedAt: openedAt || undefined,
                externalCaseNumber: externalCaseNumber || undefined,
                court: court || undefined,
                district: district || undefined,
                courtDivision: courtDivision || undefined,
                state: state ? state.toUpperCase() : undefined,
                courtLink: courtLink || undefined,
                consultationPassword: consultationPassword || undefined,
                claimValue: claimValue > 0 ? claimValue : undefined,
                responsibleFirmId: responsibleFirmId || undefined,
                cpc25Classification: cpc25Classification || undefined,
                // envia o percentual se há classificação OU percentual > 0 (parênteses deixam a precedência explícita)
                estimatedLossPercent: (cpc25Classification || estimatedLossPercent > 0)
                    ? estimatedLossPercent : undefined,
                provisionOverrideAmount: provisionOverrideAmount > 0 ? provisionOverrideAmount : undefined,
                provisionOverrideReason: provisionOverrideReason || undefined,
                description: description || undefined,
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return;
            }
            if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
                return;
            }
            toast.error(e?.message || 'Erro ao cadastrar caso jurídico');
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        caseType, setCaseType,
        pole, setPole,
        status, setStatus,
        creditorEntity, setCreditorEntity,
        openedAt, setOpenedAt,
        externalCaseNumber, setExternalCaseNumber,
        court, setCourt,
        district, setDistrict,
        courtDivision, setCourtDivision,
        state, setState,
        courtLink, setCourtLink,
        consultationPassword, setConsultationPassword,
        claimValue, setClaimValue,
        responsibleFirmId, setResponsibleFirmId,
        cpc25Classification, setCpc25Classification,
        estimatedLossPercent, setEstimatedLossPercent,
        provisionOverrideAmount, setProvisionOverrideAmount,
        provisionOverrideReason, setProvisionOverrideReason,
        description, setDescription,
        caseTypeOptions, poleOptions, statusOptions, riskClassificationOptions,
        creditorEntityOptions,
        lawFirms,
        handleCreate,
    };
}
